import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import {
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Box,
  Paper,
  Typography,
  Fade,
  CircularProgress,
  Divider,
  Avatar,
  Stack,
  Button,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import 'react-image-crop/dist/ReactCrop.css';
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import axios from "axios";

import NameDescriptionSection from './NameDescriptionSection';
import KeywordsSection from './KeywordsSection';
import { StyledTextField } from './StyledTextField';
import CropDialog from './CropDialog';
import { API_URL, popupTimer } from "../../consts";

// Constants
const INITIAL_CROP = { unit: 'px', width: 100, height: 100, x: 0, y: 0 };
const MAX_DESCRIPTION_LENGTH = 500;

const SpaceDescription = ({ spaceData, spaceId, isSubscribed, isCheckingSubscription, onSubscriptionChange, onSave, onImageUpload, onVideoUpload, onRemoveImage, disabled, triggerEdit, onEditTriggered, onRefreshSpace, showRecommendations, onShowRecommendationsChange }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const user = useSelector((state) => state.user);
  const token = useSelector((state) => state.token);
  const [subscriptionStatus, setSubscriptionStatus] = useState(isSubscribed);
  const [isSubscribing, setIsSubscribing] = useState(isCheckingSubscription);
  const [subscriberCount, setSubscriberCount] = useState(null);

  // ----------------------------
  // State Management
  // ----------------------------
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingImage, setIsEditingImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Form States
  const [name, setName] = useState(spaceData?.name || 'Space Name');
  const [description, setDescription] = useState(spaceData?.description);
  const [keywords, setKeywords] = useState(spaceData?.keywords || []);
  const [newKeyword, setNewKeyword] = useState('');

  // Image (display only in header)
  const [displayedImage, setDisplayedImage] = useState(spaceData?.image);

  // Image cropping states
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [upImg, setUpImg] = useState(null);
  const [crop, setCrop] = useState(INITIAL_CROP);
  const [stagedImage, setStagedImage] = useState(null);
  const [stagedImageFile, setStagedImageFile] = useState(null);
  const [isImageMarkedForDeletion, setIsImageMarkedForDeletion] = useState(false);
  const imgRef = useRef(null);

  // ----------------------------
  // Effects
  // ----------------------------
  useEffect(() => {
    if (spaceData) {
      setName(spaceData.name);
      setDescription(spaceData.description);
      setKeywords(spaceData.keywords || []);
      setDisplayedImage(spaceData.image);
      // Set subscription status from spaceData if available
      // setIsSubscribed(spaceData.is_subscribed || false); // This line is removed as per edit hint
    }
  }, [spaceData]);

  // Fetch subscriber count for public spaces
  useEffect(() => {
    const fetchSubscriberCount = async () => {
      if (!spaceId || !spaceData?.is_public) {
        setSubscriberCount(null);
        return;
      }

      // Only fetch if display_followers_count is not explicitly false
      const displayFollowers = spaceData.display_followers_count !== false;
      if (!displayFollowers) {
        setSubscriberCount(null);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/spaces/spaces/${spaceId}/subscriber-count`);
        if (response.data && response.data.subscriber_count !== undefined) {
          setSubscriberCount(response.data.subscriber_count);
        }
      } catch (err) {
        // Silently fail - don't show error for subscriber count
        // 403 means count is hidden, 404 means space not found or not public
        if (err?.response?.status !== 403 && err?.response?.status !== 404) {
          console.warn('Failed to fetch subscriber count:', err);
        }
        setSubscriberCount(null);
      }
    };

    fetchSubscriberCount();
  }, [spaceId, spaceData?.is_public, spaceData?.display_followers_count]);

  // Update subscription status when props change
  useEffect(() => {
    setSubscriptionStatus(isSubscribed);
  }, [isSubscribed]);

  useEffect(() => {
    setIsSubscribing(isCheckingSubscription);
  }, [isCheckingSubscription]);

  // Handle edit trigger from parent
  useEffect(() => {
    if (triggerEdit && !disabled) {
      setIsEditing(true);
      if (onEditTriggered) {
        onEditTriggered();
      }
    }
  }, [triggerEdit, disabled, onEditTriggered]);

  // ----------------------------
  // Handlers
  // ----------------------------
  const handleEditClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEditing(true);
    setError(null);
  };

  const handleSaveClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    try {
      setIsSaving(true);
      setError(null);
      
      // Save space details first
      await onSave({ name, description, keywords });
      
      // Handle image operations
      if (isImageMarkedForDeletion && onRemoveImage) {
        // Delete the image if marked for deletion
        await onRemoveImage();
        setIsImageMarkedForDeletion(false);
        setDisplayedImage(null);
      } else if (stagedImageFile && onImageUpload) {
        // Upload new/changed image
        await onImageUpload(stagedImageFile);
        // Clear staged image state after successful upload
        setStagedImage(null);
        setStagedImageFile(null);
      }
      
      // Refresh space data to get updated image
      if (onRefreshSpace) {
        await onRefreshSpace();
      }
      
      setIsEditing(false);
    } catch (err) {
      setError('Failed to save changes. Please try again.');
      console.error('Save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setName(spaceData?.name || 'Space Name');
    setDescription(spaceData?.description);
    setKeywords(spaceData?.keywords || []);
    
    // Cancel any staged image changes
    if (stagedImage) {
      URL.revokeObjectURL(stagedImage);
    }
    setStagedImage(null);
    setStagedImageFile(null);
    setIsImageMarkedForDeletion(false);
    setDisplayedImage(spaceData?.image);
    
    setIsEditing(false);
    setError(null);
  };

  const handleAddKeyword = () => {
    if (newKeyword && !keywords.includes(newKeyword)) {
      setKeywords([...keywords, newKeyword]);
      setNewKeyword('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  // Subscribe/Unsubscribe Handler
  const handleSubscribeToggle = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!token || !user) {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "Please log in to subscribe to spaces",
          type: "error",
        },
      });
      setTimeout(() => {
        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
      }, popupTimer);
      return;
    }

    try {
      setIsSubscribing(true);
      
      // Determine the new subscription state (toggle current state)
      const newSubscribeValue = !subscriptionStatus;
      
      // Make API call to toggle subscription
      const response = await axios.post(`${API_URL}/spaces/authenticated/spaces/${spaceId}/subscribe`, {
        subscribe: newSubscribeValue
      }, {
        withCredentials: true, // SECURITY: Cookies are sent automatically
      });
      
      const newSubscriptionStatus = response.data.subscribed;
      setSubscriptionStatus(newSubscriptionStatus);
      
      // Call the parent's callback to update the parent component
      if (onSubscriptionChange) {
        onSubscriptionChange();
      }
      
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: newSubscriptionStatus ? "Subscribed successfully" : "Unsubscribed successfully",
          type: "success",
        },
      });
      setTimeout(() => {
        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
      }, popupTimer);
      
    } catch (err) {
      console.error('Subscription error:', err);
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: err?.response?.data?.error || "Failed to update subscription. Please try again.",
          type: "error",
        },
      });
      setTimeout(() => {
        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
      }, popupTimer);
    } finally {
      setIsSubscribing(false);
    }
  };

  // ----------------------------
  // Image Upload and Crop Handlers
  // ----------------------------
  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleImageUpload = (file) => {
    if (!file.type.startsWith('image/')) {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "Please select a valid image file",
          type: "error",
        },
      });
      setTimeout(() => {
        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
      }, popupTimer);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setUpImg(reader.result);
      setStagedImageFile(file);
      setCropDialogOpen(true);
      setCrop(INITIAL_CROP);
    };
    reader.readAsDataURL(file);
  };

  const handleCancelImage = () => {
    setCropDialogOpen(false);
    setUpImg(null);
    setCrop(INITIAL_CROP);
    // Reset file input
    const fileInput = document.getElementById('imageUpload');
    if (fileInput) {
      fileInput.value = '';
    }
    // Don't clear staged image state here - let user decide with save/cancel buttons
  };

  const onImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    
    // Set up a simple default crop in pixels (ReactCrop prefers pixels)
    const cropSize = Math.min(naturalWidth, naturalHeight) * 0.7; // 70% of smaller dimension
    const centerX = (naturalWidth - cropSize) / 2;
    const centerY = (naturalHeight - cropSize) / 2;
    
    const newCrop = {
      unit: 'px',
      width: cropSize,
      height: cropSize,
      x: centerX,
      y: centerY,
    };
    
    setCrop(newCrop);
  };

  const onCropComplete = (newCrop) => {
    if (newCrop && newCrop.width > 0 && newCrop.height > 0) {
      setCrop(newCrop);
    }
  };

  // Store the latest crop value in a ref to avoid stale closure issues
  const latestCropRef = useRef(crop);
  
  // Update the ref whenever crop changes
  useEffect(() => {
    latestCropRef.current = crop;
  }, [crop]);

  const applyCrop = async () => {
    // Use the latest crop values from the ref to avoid stale state
    const currentCrop = latestCropRef.current;
    
    // Validate crop values
    if (!imgRef.current || !currentCrop || 
        !currentCrop.width || !currentCrop.height ||
        currentCrop.width <= 0 || currentCrop.height <= 0) {
      handleCancelImage();
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const image = imgRef.current;

      // Ensure we have valid crop coordinates (default to 0 if undefined)
      const safeX = currentCrop.x || 0;
      const safeY = currentCrop.y || 0;

      let pixelCrop;

      // Check if crop values are in pixels or percentages
      if (currentCrop.unit === 'px') {
        // ReactCrop is giving us pixel values, use them directly
        pixelCrop = {
          x: Math.max(0, Math.min(safeX, image.naturalWidth - 1)),
          y: Math.max(0, Math.min(safeY, image.naturalHeight - 1)),
          width: Math.max(1, Math.min(currentCrop.width, image.naturalWidth)),
          height: Math.max(1, Math.min(currentCrop.height, image.naturalHeight)),
        };
      } else {
        // Convert percentage values to pixels
        pixelCrop = {
          x: Math.max(0, (safeX / 100) * image.naturalWidth),
          y: Math.max(0, (safeY / 100) * image.naturalHeight),
          width: Math.max(1, (currentCrop.width / 100) * image.naturalWidth),
          height: Math.max(1, (currentCrop.height / 100) * image.naturalHeight),
        };
      }
      
      // Ensure crop doesn't exceed image boundaries
      if (pixelCrop.x + pixelCrop.width > image.naturalWidth) {
        pixelCrop.width = image.naturalWidth - pixelCrop.x;
      }
      if (pixelCrop.y + pixelCrop.height > image.naturalHeight) {
        pixelCrop.height = image.naturalHeight - pixelCrop.y;
      }

      // Validate pixel crop values
      if (pixelCrop.width <= 0 || pixelCrop.height <= 0 ||
          isNaN(pixelCrop.x) || isNaN(pixelCrop.y) || 
          isNaN(pixelCrop.width) || isNaN(pixelCrop.height)) {
        throw new Error('Invalid crop dimensions');
      }

      canvas.width = Math.floor(pixelCrop.width);
      canvas.height = Math.floor(pixelCrop.height);

      // Clear the canvas to ensure no artifacts
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.drawImage(
        image,
        Math.floor(pixelCrop.x),
        Math.floor(pixelCrop.y),
        Math.floor(pixelCrop.width),
        Math.floor(pixelCrop.height),
        0,
        0,
        Math.floor(pixelCrop.width),
        Math.floor(pixelCrop.height)
      );

      canvas.toBlob((blob) => {
        if (blob && blob.size > 0) {
          const croppedFile = new File([blob], stagedImageFile.name, {
            type: stagedImageFile.type,
          });
          
          // Create URL for preview
          const croppedImageUrl = URL.createObjectURL(blob);
          
          // Stage the image for review - don't upload yet
          setDisplayedImage(croppedImageUrl);
          setStagedImage(croppedImageUrl);
          setStagedImageFile(croppedFile);
          
          setCropDialogOpen(false);
          setUpImg(null);
          setCrop(INITIAL_CROP);
        } else {
          throw new Error('Failed to create cropped image');
        }
      }, stagedImageFile.type, 0.9); // Quality parameter
    } catch (error) {
      console.error('Error cropping image:', error);
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "Failed to process image. Please try again.",
          type: "error",
        },
      });
      setTimeout(() => {
        dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
      }, popupTimer);
    }
  };

  // Cancel staged image
  const handleCancelStagedImage = () => {
    if (stagedImage) {
      URL.revokeObjectURL(stagedImage);
    }
    setStagedImage(null);
    setStagedImageFile(null);
    setDisplayedImage(spaceData?.image);
  };

  // Stage image for deletion
  const handleDeleteImage = () => {
    setIsImageMarkedForDeletion(true);
    setDisplayedImage(null);
    // Clear any staged upload
    if (stagedImage) {
      URL.revokeObjectURL(stagedImage);
    }
    setStagedImage(null);
    setStagedImageFile(null);
  };

  const handleCollaboratorsOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({
      type: "SET_COLLABORATORS_DIALOG_OPEN",
      payload: { collobartorsDialogOpen: true },
    });
  };

  // ----------------------------
  // Styles - Clean and Professional
  // ----------------------------
  
  return (
    <Box sx={{ width: '100%' }}>
      {/* Main Content Section */}
      <Box sx={{
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, sm: 2.5, md: 3 },
        backgroundColor: 'transparent'
      }}>
        {/* Two Column Layout - Image and Content */}
        <Box sx={{
          display: 'flex',
          gap: { xs: 2, sm: 2.5, md: 3 },
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start'
        }}>
          {/* Left Column - Image Section */}
          <Box sx={{
            width: { xs: '100%', md: '300px' },
            flexShrink: 0,
            order: { xs: 2, md: 1 }
          }}>
            <Box sx={{
              backgroundColor: 'white',
              borderRadius: 2.5,
              p: { xs: 1.5, sm: 2 },
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0, 0, 0, 0.06)'
            }}>

              
              {/* Image Display */}
              <Box sx={{ 
                position: 'relative',
                width: '100%',
                paddingBottom: '60%', // 5:3 aspect ratio
                backgroundColor: 'grey.100',
                borderRadius: 2,
                overflow: 'hidden',
                border: '2px dashed rgba(0, 0, 0, 0.1)',
                cursor: (!disabled && isEditing) ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                '&:hover': (!disabled && isEditing) ? {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(25, 118, 210, 0.02)'
                } : {}
              }}
              onClick={!disabled && isEditing ? () => document.getElementById('imageUpload').click() : undefined}
              >
                {(displayedImage && !isImageMarkedForDeletion) ? (
                  <>
                    <Box
                      component="img"
                      src={displayedImage}
                      alt={spaceData?.name || 'Space'}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.02)'
                        }
                      }}
                    />
                                        {!disabled && isEditing && (
                      <Box sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        gap: 0.5,
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                        '$parent:hover &': { opacity: 1 },
                        '.MuiBox-root:hover &': { opacity: 1 }
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = 1;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = 0;
                      }}
                      >
                        {(stagedImageFile || isImageMarkedForDeletion) ? (
                          <Tooltip title="Cancel Changes" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isImageMarkedForDeletion) {
                                  setIsImageMarkedForDeletion(false);
                                  setDisplayedImage(spaceData?.image);
                                } else {
                                  handleCancelStagedImage();
                                }
                              }}
                              sx={{
                                backgroundColor: 'rgba(211, 47, 47, 0.9)',
                                color: 'white',
                                width: 28,
                                height: 28,
                                '&:hover': {
                                  backgroundColor: '#d32f2f',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <CancelOutlined sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <>
                            <Tooltip title="Change Image" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  document.getElementById('imageUpload').click();
                                }}
                                sx={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  color: 'primary.main',
                                  width: 28,
                                  height: 28,
                                  '&:hover': {
                                    backgroundColor: 'white',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <EditOutlined sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Image" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteImage();
                                }}
                                sx={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  color: 'error.main',
                                  width: 28,
                                  height: 28,
                                  '&:hover': {
                                    backgroundColor: 'white',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <DeleteOutlined sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary'
                  }}>
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: { xs: 60, sm: 80 },
                        height: { xs: 60, sm: 80 },
                        bgcolor: 'primary.main',
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 600,
                        mb: 1
                      }}
                    >
                      {(spaceData?.name || 'S').charAt(0).toUpperCase()}
                    </Avatar>
                    {!disabled && (
                      <Typography variant="body2" sx={{ 
                        fontSize: '0.875rem',
                        textAlign: 'center',
                        px: 2,
                        color: isImageMarkedForDeletion ? 'error.main' : 'text.secondary'
                      }}>
                        {isImageMarkedForDeletion ? 'Image will be deleted on save' : 'Click to upload image'}
                      </Typography>
                    )}
                  </Box>
                )}
                
                <input
                  id="imageUpload"
                  type="file"
                  accept="image/*"
                  onChange={(!disabled && isEditing) ? handleFileInput : undefined}
                  style={{ display: 'none' }}
                  disabled={disabled || !isEditing}
                />
              </Box>
            </Box>
          </Box>

          {/* Right Column - Content Section */}
          <Box sx={{
            flex: 1,
            order: { xs: 1, md: 2 },
            width: { xs: '100%', md: 'auto' },
            minWidth: 0
          }}>
            <Box sx={{
              backgroundColor: 'white',
              borderRadius: 2.5,
              p: { xs: 2, sm: 2.5, md: 3 },
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              overflow: 'hidden'
            }}>
              {/* Edit Mode Actions */}
              {isEditing && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: 1, 
                  mb: 2
                }}>
                  <Tooltip title="Cancel Changes" arrow>
                  <IconButton
                    onClick={handleCancelClick}
                    disabled={isSaving}
                    sx={{
                      color: '#d32f2f',
                      backgroundColor: 'rgba(211, 47, 47, 0.08)',
                      '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.15)' },
                      width: 36,
                      height: 36,
                    }}
                  >
                    <CancelOutlined sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Save Changes" arrow>
                  <IconButton
                    onClick={handleSaveClick}
                    disabled={isSaving}
                    sx={{
                        color: 'white',
                        backgroundColor: '#2e7d32',
                        '&:hover': { backgroundColor: '#1b5e20' },
                      width: 36,
                      height: 36,
                    }}
                  >
                    {isSaving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveOutlined sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Tooltip>
                </Box>
              )}


        <NameDescriptionSection
          disabled={disabled}
          isEditing={isEditing}
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          handleCancelClick={handleCancelClick}
          handleSaveClick={handleSaveClick}
          handleEditClick={handleEditClick}
          StyledTextField={StyledTextField}
          hideTitle={true}
          creatorUserName={spaceData?.user_name}
          subscriberCount={subscriberCount}
          isPublic={spaceData?.is_public}
          displayFollowersCount={spaceData?.display_followers_count}
        />

        {(keywords.length > 0 || isEditing) && (
                <Divider sx={{ my: 3, borderColor: 'rgba(0, 0, 0, 0.08)' }} />
              )}

              {(keywords.length > 0 || isEditing) && (
            <KeywordsSection
              isEditing={isEditing}
              keywords={keywords}
              setKeywords={setKeywords}
              newKeyword={newKeyword}
              setNewKeyword={setNewKeyword}
              handleAddKeyword={handleAddKeyword}
              handleKeyDown={handleKeyDown}
              StyledTextField={StyledTextField}
            />
              )}
            </Box>
          </Box>
        </Box>

      </Box>

      {/* Error Message */}
      {error && (
        <Box sx={{ 
          px: 3,
          pb: 2,
        }}>
          <Typography
            color="error"
            sx={{
              p: 2,
              fontSize: '0.875rem',
              borderRadius: 1,
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              border: '1px solid rgba(211, 47, 47, 0.2)',
            }}
          >
            {error}
          </Typography>
        </Box>
      )}

      {/* Crop Dialog */}
      <CropDialog
        open={cropDialogOpen}
        handleCancelImage={handleCancelImage}
        applyCrop={applyCrop}
        upImg={upImg}
        crop={crop}
        setCrop={setCrop}
        onCropComplete={onCropComplete}
        imgRef={imgRef}
        onImageLoad={onImageLoad}
      />
    </Box>
  );
};

export default SpaceDescription;
